<template>
  <header>
    <AppNavTop class="hidden sm:block" />

    <!-- Desktop Nav -->
    <nav class="nav-desktop">
      <div class="wrapper ">
        <!-- SVG background shape on menu - hidden on mobile -->
        <img
          loading="lazy"
          src="@/assets/images/global/app-nav/nav-shape-divider.svg"
          class="shape-divider"
          alt="Navigation menu shape divider"
        >

        <!-- Left Nav -->
        <div class="col-left">
          <AppLink
            class="relative"
            :to="{name: 'app_event_list'}"
          >
            <span class="sr-only">SportPix</span>

            <img
              loading="lazy"
              class="logo"
              src="@/assets/images/global/app-nav/sportpix-logo.svg"
              alt="SportPix Logo"
            >
          </AppLink>
        </div>

        <!-- Center Nav - Main Menu Links -->
        <div class="col-center">
          <menu>
            <li
              v-for="(link, index) in menuLinks"
              :key="index"
            >
              <AppLink
                :to="link.to"
                class="menu-link"
              >
                {{ link.name }}
              </AppLink>
            </li>
          </menu>
        </div>

        <!-- Right Nav - User Tools -->
        <div class="col-right">
          <CartButton :cart-items="cartStore?.cart?.items?.length" />

          <template v-if="authStore.user && authStore.isAuthenticated">
            <AppLink
              v-if="!authStore.user?.isAdmin"
              class="my-orders menu-link"
              :to="{ name: 'app_orders' }"
            >
              My Orders
            </AppLink>

            <!-- Profile Dropdown -->
            <AppDropdown
              :trigger-text="profileDropdownTriggerText"
              :icon="faChevronDown"
              class="header-dropdown whitespace-nowrap"
            >
              <AppDropdownItem :to="{ name: 'app_profile' }">
                My Profile
              </AppDropdownItem>

              <AppDropdownItem @click="logout">
                Logout
              </AppDropdownItem>
            </AppDropdown>
          </template>

          <template v-else>
            <AppButton
              class="menu-link sm:block hidden"
              @click="handleShowAuthenticationModal(IntentType.Login)"
            >
              Login
            </AppButton>

            <AppButton
              class="menu-link sm:block hidden"
              @click="handleShowAuthenticationModal(IntentType.Register)"
            >
              Register
            </AppButton>
          </template>
        </div>

        <!-- Mobile Menu Toggle -->
        <button
          type="button"
          class="menu-toggle"
          @click="showMobileMenu = !showMobileMenu"
        >
          <span class="sr-only">Open main menu</span>

          <FontAwesomeIcon
            size="xl"
            :icon="faBars"
          />
        </button>
      </div>
    </nav>

    <AppNavMobile
      v-model="showMobileMenu"
      :cart-items="cartStore?.cart?.items"
      :user="authStore.user ? profileDropdownTriggerText : ''"
      @login="handleShowAuthenticationModal(IntentType.Login)"
      @register="handleShowAuthenticationModal(IntentType.Register)"
      @logout="logout"
    />

    <!-- Authentication modal -->
    <Transition name="fade">
      <AuthenticationModal
        v-if="showAuthenticationModal"
        :intent="intent"
        @close="showAuthenticationModal = false"
      />
    </Transition>
  </header>
</template>

<script setup>
import axios, { HttpStatusCode } from 'axios';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

import { AppButton, AppDropdown, AppDropdownItem, AppLink, AppNavMobile, AppNavTop } from '@/components/app';
import { CartButton } from '@/components/buttons';
import { AuthenticationModal } from '@/components/modals';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faBars, faChevronDown } from '@fortawesome/pro-solid-svg-icons';

import { IntentType } from '@/const';
import { useAuthStore, useCartStore } from '@/store';

const menuLinks = [
  { name: 'About SportPix', to: { name: 'app_about_us' } },
  { name: 'Contact Us', to: { name: 'app_contact_us' } },
];

const router = useRouter();
const authStore = useAuthStore();
const cartStore = useCartStore();

const showAuthenticationModal = ref(false);
const showMobileMenu = ref(false);
const intent = ref(null);
const profileDropdownTriggerText = computed(() => authStore.user?.firstName || 'My Account');

const logout = async () => {
  const response = await axios.post('auth/logout');

  if (response.data.statusCode === HttpStatusCode.Ok) {
    authStore.user = null;
    cartStore.cart = null;

    await router.push({ name: 'app_event_list' });
  }
};

const handleShowAuthenticationModal = (intentType) => {
  showAuthenticationModal.value = true;
  intent.value = intentType;
};
</script>
